import { Routes } from '@angular/router';
import { clientRoutesNames } from '@app/client/client.routes.names';
import { IsAccountValidGuard } from '@app/core/guards/base/is-account-valid.guard';
import { SigninCanActivateGuard } from '@app/core/guards/signin.can-activate.guard';
import { SubscriptionUpdateGuard } from '@app/core/guards/subscription-update.guard';
import { SwitchLanguageGuard } from '@app/core/guards/switch-language.guard';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { FilesExpiredComponent } from './files-expired/files-expired.component';
import { ServiceUnavailableComponent } from './service-unavailable/service-unavailable.component';
import { InvalidConfigurationComponent } from '@app/client/invalid-configuration/invalid-configuration.component';
import { DeletedComponent } from './deleted/deleted.component';
import { SuspendedComponent } from './suspended/suspended.component';
import { UnsupportedComponent } from './unsupported/unsupported.component';
import { UsageExceededComponent } from '@app/client/usage-exceeded/usage-exceeded.component';
import { ViewerGuard } from '@app/core/guards/viewer.guard';
import { PaypalGuard } from '@app/core/guards/paypal.guard';
import { RedirectSsoGuard } from '@app/core/guards/redirect-sso.guard';
import { RequestExpiredComponent } from './request-expired/request-expired.component';
import { LoadServicesGuard } from '@app/core/guards/base/load-services.guard';
import { ReportGuard } from '@app/core/guards/report.guard';

export function routesFactory(deviceDetector: DeviceDetectorService): Routes {
    return [
        {
            path: clientRoutesNames.SERVICE_UNAVAILABLE,
            pathMatch: 'full',
            component: ServiceUnavailableComponent,
        },
        {
            path: clientRoutesNames.INVALID_CONFIGURATION,
            pathMatch: 'full',
            component: InvalidConfigurationComponent,
        },
        {
            path: clientRoutesNames.QUOTA_EXCEEDED,
            pathMatch: 'full',
            component: UsageExceededComponent,
        },
        {
            path: clientRoutesNames.DELETED,
            pathMatch: 'full',
            component: DeletedComponent,
        },
        {
            path: clientRoutesNames.UNSUPPORTED,
            pathMatch: 'full',
            component: UnsupportedComponent,
        },
        {
            path: clientRoutesNames.DROPBOX_EXPIRED,
            pathMatch: 'full',
            canActivate: [ViewerGuard],
            component: RequestExpiredComponent
        },
        {
            path: clientRoutesNames.REPORT,
            canActivate: [ReportGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/report/report.module').then(m => m.ReportModule),
        },
        {
            path: clientRoutesNames.SUSPENDED,
            pathMatch: 'full',
            component: SuspendedComponent,
        },
        {
            path: 'fr',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'en',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'es',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'it',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'pt',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'de',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: clientRoutesNames.PAGE_NOT_FOUND,
            component: PageNotFoundComponent,
        },
        {
            path: clientRoutesNames.TRANSFER_EXPIRED,
            canActivate: [ViewerGuard],
            component: FilesExpiredComponent
        },
        {
            path: clientRoutesNames.SUBSCRIPTION_UPDATE,
            canActivate: [SubscriptionUpdateGuard],
            loadChildren: () => import('@app/client/subscription-update/subscription-update.module').then(m => m.SubscriptionUpdateModule),
        },
        {
            path: clientRoutesNames.ACCOUNT,
            loadChildren: () => {
                return deviceDetector.isMobile() ?
                    import('@app/client/account-mobile/account-mobile.module').then(m => m.AccountMobileModule) :
                    import('@app/client/account/account.module').then(m => m.AccountModule);
            }
        },
        {
            path: clientRoutesNames.SIGNUP,
            loadChildren: () => import('@app/client/signup/signup.module').then(m => m.SignupModule),
        },
        {
            path: clientRoutesNames.UPLOADER,
            loadChildren: () => {
                return deviceDetector.isMobile() ?
                    import('@app/client/uploader-mobile/uploader-mobile.module').then(m => m.UploaderMobileModule) :
                    import('@app/client/uploader/uploader.module').then(m => m.UploaderModule);
            }
        },
        {
            path: clientRoutesNames.REDIRECT_SSO,
            canActivate: [RedirectSsoGuard],
            loadChildren: () => import('@app/client/redirect-sso/redirect-sso.module').then(m => m.RedirectSsoModule),
        },
        {
            path: clientRoutesNames.REDIRECT,
            canActivate: [IsAccountValidGuard],
            loadChildren: () => import('@app/client/redirect/redirect.module').then(m => m.RedirectModule),
        },
        {
            path: clientRoutesNames.SIGNIN,
            canActivate: [SigninCanActivateGuard],
            loadChildren: () => {
                return deviceDetector.isMobile() ?
                    import('@app/client/signin-mobile/signin-mobile.module').then(m => m.SigninMobileModule) :
                    import('@app/client/signin/signin.module').then(m => m.SigninModule);
            }
        },
        {
            path: clientRoutesNames.SIGNOUT,
            loadChildren: () => import('@app/client/signout/signout.module').then(m => m.SignoutModule),
        },
        {
            path: clientRoutesNames.PREVIEW,
            canActivate: [ViewerGuard],
            data: { moduleName: 'ViewerModule' },
            loadChildren: () => import('@app/client/viewer/viewer.module').then(m => m.ViewerModule),
        },
        {
            path: clientRoutesNames.PAYPAL,
            canActivate: [PaypalGuard],
            loadChildren: () => import('@app/client/paypal/paypal.module').then(m => m.PaypalModule),
        },
        {
            path: '**',
            redirectTo: '404'
        }
    ];
}
